/**
 * @fileoverview Name who covers one class session — upcoming or already past
 * (docs/substitute-requests.md).
 *
 * The cover is either someone on the org's substitute roster (no Hans account;
 * they are emailed the session and a link to write its notes) or one of the
 * org's own instructors (the session appears in their portal and they owe its
 * notes). Either way the session stops being the regular instructor's: they
 * are not chased for its notes and it does not count toward their teaching.
 *
 * Works on a session that already happened as readily as one that has not: a
 * substitute is often recorded after the fact. Once the substitute has written
 * the session's notes the cover cannot be cleared or changed here — the lesson
 * is teaching evidence — which the server enforces and this says.
 *
 * The people to choose from are listed in the dialog, a radio against each,
 * with a search once there are more than eight (C3, decided Nov 1, 2026, the
 * same list as the iPhone's sheet). The org Calendar opens it without a
 * roster; it reads the organization's then.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, TextTabs } from "@/components/studio/primitives";
import { dayLabel } from "@/lib/orgReporting";

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

/** More people than this and the list gets a search above it. */
const SEARCH_FROM = 8;

/** Everyone of one kind, a radio against each; past eight, a search above them. */
function PersonList({ name, label, people, value, onChange, searchLabel, emptyText, testId }) {
  const [query, setQuery] = useState("");
  const words = query.trim().toLowerCase();
  const shown = words ? people.filter((p) => `${p.label} ${p.sub || ""}`.toLowerCase().includes(words)) : people;
  return (
    <div className="mt-4">
      {people.length > SEARCH_FROM && (
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchLabel}
          aria-label={searchLabel}
          data-testid={`${testId}-search`}
          className={`${inputClass} mb-2`}
        />
      )}
      <div
        role="radiogroup"
        aria-label={label}
        data-testid={testId}
        className="max-h-[280px] overflow-y-auto rounded-[12px] border border-[var(--studio-divider-strong)]"
      >
        {shown.length === 0 ? (
          <p className="px-4 py-3 font-sans text-[14px] text-[var(--text-tertiary)]" data-testid={`${testId}-empty`}>
            {people.length ? "Nobody matches." : emptyText}
          </p>
        ) : (
          shown.map((p) => (
            <label
              key={p.id}
              data-testid={`${testId}-option-${p.id}`}
              className="flex cursor-pointer items-center gap-3 border-t border-[var(--studio-divider)] px-4 py-3 first:border-t-0 hover:bg-[var(--studio-overlay)]"
            >
              <input
                type="radio"
                name={name}
                value={p.id}
                checked={value === p.id}
                onChange={() => onChange(p.id)}
                className="h-4 w-4 shrink-0 accent-[var(--accent-yellow)]"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-sans text-[15px] font-semibold text-[var(--text-primary)]">{p.label}</span>
                {p.sub && <span className="block truncate font-sans text-[13px] text-[var(--text-tertiary)]">{p.sub}</span>}
              </span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}

/** "4:00 PM" from "16:00", or nothing. */
function clock(time) {
  if (!time) return "";
  const [h, m] = String(time).split(":").map(Number);
  if (Number.isNaN(h)) return time;
  return new Date(2000, 0, 1, h, m || 0).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export default function AssignSubstituteDialog({
  open,
  onOpenChange,
  classId,
  session,
  substitutes,
  excludeUserIds = [],
  onAssigned,
}) {
  const current = session?.substitute || null;
  const [kind, setKind] = useState("roster");
  const [substituteId, setSubstituteId] = useState("");
  const [instructorId, setInstructorId] = useState("");
  const [instructors, setInstructors] = useState(null);
  const [fetchedRoster, setFetchedRoster] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const written = Boolean(current?.lesson_id);

  useEffect(() => {
    if (!open) return;
    setKind(current?.kind === "instructor" ? "instructor" : "roster");
    setSubstituteId(current?.kind === "roster" ? current.substitute_id || "" : "");
    setInstructorId(current?.kind === "instructor" ? current.instructor_user_id || "" : "");
    setNote(current?.note || "");
  }, [open, current]);

  useEffect(() => {
    if (!open || instructors) return;
    orgApi
      .get("/org/instructors")
      .then((res) => setInstructors(res.data?.instructors || []))
      .catch(() => setInstructors([]));
  }, [open, instructors]);

  // A caller with no roster to hand (the org Calendar) leaves it to the dialog.
  useEffect(() => {
    if (!open || substitutes || fetchedRoster) return;
    orgApi
      .get("/org/substitutes")
      .then((res) => setFetchedRoster((res.data?.substitutes || []).filter((x) => x.active !== false)))
      .catch(() => setFetchedRoster([]));
  }, [open, substitutes, fetchedRoster]);
  const roster = substitutes || fetchedRoster;

  // Who covers it now stays first in its list, even once they are no longer
  // on the roster or among the instructors.
  const rosterPeople = (roster || []).map((x) => ({ id: x.substitute_id, label: x.name, sub: x.email || "No email on file" }));
  if (current?.kind === "roster" && current.substitute_id && roster && !rosterPeople.some((x) => x.id === current.substitute_id)) {
    rosterPeople.unshift({ id: current.substitute_id, label: current.name || "Substitute", sub: "No longer on the roster" });
  }
  const instructorPeople = (instructors || [])
    .filter((i) => i.user_id && !excludeUserIds.includes(i.user_id))
    .map((i) => ({ id: i.user_id, label: i.name || i.email, sub: i.name ? i.email : null }));
  if (
    current?.kind === "instructor" &&
    current.instructor_user_id &&
    instructors &&
    !instructorPeople.some((x) => x.id === current.instructor_user_id)
  ) {
    instructorPeople.unshift({ id: current.instructor_user_id, label: current.name || "Instructor", sub: "No longer teaching here" });
  }
  const chosen = kind === "roster" ? substituteId : instructorId;

  const save = async (clear = false) => {
    if (!clear && !chosen) {
      toast.error("Choose who covers it");
      return;
    }
    setSaving(true);
    try {
      await orgApi.put(`/org/classes/${classId}/sessions/substitute`, {
        date: session.date,
        occurrence_id: session.occurrence_id || null,
        substitute_id: clear || kind !== "roster" ? null : substituteId,
        instructor_user_id: clear || kind !== "instructor" ? null : instructorId,
        note: clear ? null : note.trim() || null,
        notify: true,
      });
      toast.success(clear ? "Substitute cleared" : "Substitute assigned");
      onAssigned?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't save that"));
    } finally {
      setSaving(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      // The meeting the cover was saved against: given only a date, the server
      // takes that day's first meeting, which is wrong for a class meeting twice.
      await orgApi.post(`/org/classes/${classId}/sessions/substitute/resend`, {
        date: session.date,
        ...(session.occurrence_id ? { occurrence_id: session.occurrence_id } : {}),
      });
      toast.success(`Sent ${current.name} a fresh link`);
      onAssigned?.();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't send the link"));
    } finally {
      setResending(false);
    }
  };

  if (!session) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">Who covers this session?</DialogTitle>
        </DialogHeader>

        <p className="font-sans text-[14px] text-[var(--text-secondary)]" data-testid="assign-when">
          {dayLabel(session.date, { year: true })}
          {session.time_local ? ` at ${clock(session.time_local)}` : ""}
        </p>

        {written ? (
          <p className="mt-4 font-sans text-[14px] text-[var(--text-secondary)]" data-testid="assign-written">
            {current.name} has written this session&rsquo;s notes, so the cover can no longer be changed.
          </p>
        ) : (
          <>
            <TextTabs
              testId="assign-kind"
              className="mt-4"
              value={kind}
              onChange={setKind}
              options={[
                { id: "roster", label: "Substitute", testId: "assign-kind-roster" },
                { id: "instructor", label: "One of your instructors", testId: "assign-kind-instructor" },
              ]}
            />

            {kind === "roster" ? (
              !roster ? (
                <p data-testid="assign-roster-loading" className="mt-4 font-sans text-[14px] text-[var(--text-tertiary)]">
                  Loading…
                </p>
              ) : rosterPeople.length === 0 ? (
                <p data-testid="assign-no-subs" className="mt-4 font-sans text-[14px] text-[var(--text-tertiary)]">
                  No substitutes on file yet. Add one from the program page first.
                </p>
              ) : (
                <>
                  <PersonList
                    name="assign-substitute"
                    label="Substitute"
                    people={rosterPeople}
                    value={substituteId}
                    onChange={setSubstituteId}
                    searchLabel="Search substitutes"
                    emptyText="No substitutes on file yet."
                    testId="assign-substitute"
                  />
                  <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                    They&rsquo;re emailed the session&rsquo;s details and a link to read the plan and write its notes.
                  </p>
                </>
              )
            ) : (
              <>
                {instructors ? (
                  <PersonList
                    name="assign-instructor"
                    label="Instructor"
                    people={instructorPeople}
                    value={instructorId}
                    onChange={setInstructorId}
                    searchLabel="Search instructors"
                    emptyText="There is nobody else to choose from yet."
                    testId="assign-instructor"
                  />
                ) : (
                  <p data-testid="assign-instructor-loading" className="mt-4 font-sans text-[14px] text-[var(--text-tertiary)]">
                    Loading…
                  </p>
                )}
                <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                  The session appears in their Hans portal and they write its notes there. They don&rsquo;t need to
                  be on this program.
                </p>
              </>
            )}

            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="A note for the substitute (optional)"
              rows={3}
              aria-label="Note for the substitute"
              data-testid="assign-note"
              className={`${inputClass} mt-4`}
            />
          </>
        )}

        {current?.kind === "roster" && (
          <p className="mt-3 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="assign-link-state">
            {current.link_active ? "Their link is active." : "Their link isn't active."}
            {current.email && (
              <>
                {" "}
                <button
                  type="button"
                  onClick={resend}
                  disabled={resending}
                  data-testid="assign-resend"
                  className="font-medium text-[var(--accent-yellow-text)] hover:underline disabled:opacity-50"
                >
                  {resending ? "Sending…" : "Send a fresh link"}
                </button>
              </>
            )}
          </p>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-3">
          {!written && (
            <Button
              onPress={() => save(false)}
              disabled={saving || !chosen}
              data-testid="assign-submit"
            >
              {saving ? "Saving…" : current && current.kind !== "unnamed" ? "Save" : "Assign"}
            </Button>
          )}
          {current && !written && (
            <button
              type="button"
              onClick={() => save(true)}
              data-testid="assign-clear"
              className="font-sans text-[14px] text-[var(--state-danger)] hover:underline"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
          >
            {written ? "Close" : "Cancel"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
